import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const NOTICE_NEW_DAYS = 10;

function getAuthToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.token ? String(stored.token) : '';
  } catch {
    return '';
  }
}

function authHdr() {
  const t = getAuthToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}

function fmtDateFull(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function isNoticeNew(receivedAt) {
  if (!receivedAt) return false;
  try {
    const diffMs = Date.now() - new Date(receivedAt).getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= NOTICE_NEW_DAYS;
  } catch {
    return false;
  }
}

function DashboardNoticeModal({ notice, onClose }) {
  useEffect(() => {
    if (!notice) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [notice]);

  if (!notice) return null;

  return createPortal(
    <div className="dashboard-notice-modal" onClick={onClose} role="presentation">
      <div
        className="dashboard-notice-modal__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-notice-modal-title"
      >
        <div className="dashboard-notice-modal__head">
          <div className="dashboard-notice-modal__head-top">
            <span className="dashboard-notice-item__scope-group">
              <span className="dashboard-notice-item__scope">공지사항</span>
              {isNoticeNew(notice.receivedAt) ? (
                <span className="dashboard-notice-item__new-badge">N</span>
              ) : null}
            </span>
          </div>
          <h3 id="dashboard-notice-modal-title" className="dashboard-notice-modal__title">
            {notice.title}
          </h3>
          <p className="dashboard-notice-modal__date">{fmtDateFull(notice.receivedAt)}</p>
        </div>
        <div className="dashboard-notice-modal__body">
          <p className="dashboard-notice-modal__content">
            {notice.body || '(내용 없음)'}
          </p>
        </div>
        <button type="button" className="dashboard-notice-modal__close" onClick={onClose}>
          확인
        </button>
      </div>
    </div>,
    document.body,
  );
}

function mapHistoryItem(h, readMap = {}) {
  const seq = h.seq;
  const readAt = readMap[seq];
  return {
    seq,
    extSeq: seq,
    title: h.title || 'MRT 공지',
    body: h.body || '',
    sender: h.sender || '',
    pushType: 'broadcast',
    read: !!readAt,
    readAt: readAt || null,
    receivedAt: h.createdAt,
    isBroadcastOnly: true,
    pushSubType: h.pushSubType || 'broadcast',
  };
}

export default function DashboardNoticeBanner({ refreshToken = 0 }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const fetchNotices = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setNotices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const apiUrl = getApiBaseUrl();
      const q = new URLSearchParams({ push_type: 'broadcast', limit: '3', offset: '0' });
      const [resHistory, resMine] = await Promise.all([
        fetch(`${apiUrl}/api/erp/push/history?${q}`, { headers: authHdr() }),
        fetch(`${apiUrl}/api/erp/push/my-broadcasts?limit=20`, { headers: authHdr() }),
      ]);
      const historyData = resHistory.ok ? await resHistory.json() : { items: [] };
      const mineData = resMine.ok ? await resMine.json() : { items: [] };

      const readMap = {};
      (mineData.items || []).forEach((n) => {
        if (n.isRead && n.readAt) readMap[n.seq] = n.readAt;
        else if (n.isRead) readMap[n.seq] = true;
      });

      const broadcastOnly = (historyData.items || []).filter(
        (h) => (h.pushSubType || 'broadcast') !== 'individual',
      );
      setNotices(broadcastOnly.slice(0, 3).map((h) => mapHistoryItem(h, readMap)));
    } catch {
      setNotices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices, refreshToken]);

  const openDetail = async (n) => {
    setDetail(n);
    if (!n.read && n.extSeq) {
      try {
        await fetch(`${getApiBaseUrl()}/api/erp/push/read`, {
          method: 'POST',
          headers: authHdr(),
          body: JSON.stringify({ push_seq: n.extSeq }),
        });
        setNotices((prev) => prev.map((item) => (
          item.seq === n.seq ? { ...item, read: true } : item
        )));
      } catch { /* ignore */ }
    }
  };

  if (loading) return null;

  if (!notices.length) return null;

  return (
    <>
      <section className="dashboard-notice-banner" aria-label="공지사항">
        <ul className="dashboard-notice-banner__list">
          {notices.map((n) => (
            <li key={n.seq}>
              <button
                type="button"
                className={`dashboard-notice-item${n.read ? ' dashboard-notice-item--read' : ''}`}
                onClick={() => openDetail(n)}
              >
                <div className="dashboard-notice-item__row">
                  <span className="dashboard-notice-item__scope-group">
                    <span className="dashboard-notice-item__scope">공지사항</span>
                    {isNoticeNew(n.receivedAt) ? (
                      <span className="dashboard-notice-item__new-badge" aria-label="새 공지">N</span>
                    ) : null}
                  </span>
                  <span className="dashboard-notice-item__title" title={n.title}>{n.title}</span>
                  <span className="dashboard-notice-item__date">{fmtTime(n.receivedAt)}</span>
                </div>
                {n.body ? (
                  <p className="dashboard-notice-item__body">{n.body}</p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <DashboardNoticeModal notice={detail} onClose={() => setDetail(null)} />
    </>
  );
}
